/**
 * Custom Link Node for Lexical
 * Extends LinkNode with additional functionality for AST compatibility
 */

import { LinkNode, SerializedLinkNode } from '@lexical/link'
import { $applyNodeReplacement, DOMConversionMap, DOMExportOutput, EditorConfig, LexicalNode, Spread } from 'lexical'

export type SerializedCustomLinkNode = Spread<
  {
    url: string
    target?: string
    rel?: string
  },
  SerializedLinkNode
>

export class CustomLinkNode extends LinkNode {
  static getType(): string {
    return 'custom-link'
  }

  static clone(node: CustomLinkNode): CustomLinkNode {
    return new CustomLinkNode(
      node.__url,
      { target: node.__target || undefined, rel: node.__rel || undefined },
      node.__key,
    )
  }

  constructor(url: string, attributes?: { target?: string; rel?: string }, key?: string) {
    super(url, attributes, key)
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.style.color = 'var(--editor-link)'
    dom.style.textDecoration = 'underline'
    dom.title = 'Cmd/Ctrl+click to open link'
    dom.addEventListener('click', (event: Event) => {
      const e = event as MouseEvent
      if (!e.metaKey && !e.ctrlKey) {
        return
      }
      const href = (e.currentTarget as HTMLAnchorElement).href
      if (!href) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      window.open(href, '_blank', 'noopener,noreferrer')
    })
    return dom
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: () => ({
        conversion: (element: HTMLElement) => {
          const anchor = element as HTMLAnchorElement
          const url = anchor.href || ''
          const target = anchor.target || undefined
          const rel = anchor.rel || undefined
          return {
            node: $createCustomLinkNode(url, { target, rel }),
          }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('a')
    element.href = this.__url
    if (this.__target) {
      element.target = this.__target
    }
    if (this.__rel) {
      element.rel = this.__rel
    }
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomLinkNode): CustomLinkNode {
    return $createCustomLinkNode(serializedNode.url, {
      target: serializedNode.target,
      rel: serializedNode.rel,
    })
  }

  exportJSON(): SerializedCustomLinkNode {
    return {
      ...super.exportJSON(),
      type: 'custom-link',
      url: this.__url,
      target: this.__target || undefined,
      rel: this.__rel || undefined,
    }
  }
}

export function $createCustomLinkNode(url: string, attributes?: { target?: string; rel?: string }): CustomLinkNode {
  return $applyNodeReplacement(new CustomLinkNode(url, attributes))
}

export function $isCustomLinkNode(node: LexicalNode | null | undefined): node is CustomLinkNode {
  return node instanceof CustomLinkNode
}
